import {
	FingerprintHelper,
	ValidationError,
	ValidationErrorCode,
	ValidationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/utils';
import { SegmentedInputRef, useRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { AppHelper, LoggerHelper } from '@helpers';
import { useLoader, useReactiveForm, useTranslation } from '@hooks';
import { useSearchParamsStore, useServicesStore } from '@store';
import { CredentialsChallenge, endpoints } from '@types';

const ALLOWED_CHARS_REGEX = new RegExp('[0-9]');

export const resources = {
	title: 'screens.updateCredentials.multichannel.form.title',
	input0: {
		label: 'screens.updateCredentials.multichannel.form.input0.label',
	},
	input1: {
		label: 'screens.updateCredentials.multichannel.form.input1.label',
	},
	input2: {
		label: 'screens.updateCredentials.multichannel.form.input2.label',
	},
	hint: {
		noSequentialNumbers: 'screens.updateCredentials.multichannel.form.hint.noSequentialNumbers',
		noRepeatedNumbers: 'screens.updateCredentials.multichannel.form.hint.noRepeatedNumbers',
		requiredLength: 'screens.updateCredentials.multichannel.form.hint.requiredLength',
	},
	validations: {
		multichannelMismatch: 'screens.updateCredentials.multichannel.form.validations.multichannelMismatch',
	},
	button: {
		continue: 'screens.updateCredentials.multichannel.button.continue',
	},
};

type MultichannelValidations = {
	isValid: boolean;
	required: ValidationError<string> | undefined;
	noSequentialNumbers: ValidationError<string> | undefined;
	noRepeatedNumbers: ValidationError<string> | undefined;
	allowedChars: ValidationError<string> | undefined;
	requiredLength: ValidationError<string> | undefined;
};

type MultichannelFormValues = {
	oldMultichannel?: string | null;
	multichannel?: string | null;
	confirmMultichannel?: string | null;
};

export const useMultichannelFormHelper = () => {
	const { t } = useTranslation();
	const globalLoader = useLoader('global');

	const language = useSearchParamsStore((state) => state.language);
	const accountLogin = useServicesStore((state) => state.accountLogin);
	const accountChallenge = useServicesStore((state) => state.accountChallenge);
	const accountUserCredentialsErrorMessage = useServicesStore((state) => state.accountUserCredentials?.errorMessage);

	const confirmMultichannelRef = useRef<SegmentedInputRef>(null);

	const { getCFPInputProps, submitForm, setErrors, isValid } = useReactiveForm({
		name: 'multichannelForm',
		validate: (values: MultichannelFormValues) => {
			const { multichannel, confirmMultichannel } = values;
			/* custom validation:
                         - confirmMultichannel should be equal to multichannel */
			let confirmMultichannelErrorMessage = '';
			if (multichannel && confirmMultichannel && multichannel !== confirmMultichannel) {
				confirmMultichannelErrorMessage = t(resources.validations.multichannelMismatch);
			}

			return {
				confirmMultichannel: confirmMultichannelErrorMessage,
			};
		},
		onSubmit: (values) => {
			handleFormSubmit(values);
		},
		options: { validations: { allowErrorsOnLaunch: false } },
	});

	const handleMultichannelChange = (value = '') => {
		getCFPInputProps('multichannel').onChange?.(value);
		getCFPInputProps('confirmMultichannel').onChange?.('');
		setErrors({});
	};

	const handleMultichannelComplete = (value: string) => {
		const multichannelValidations = validateMultichannel(value);
		if (multichannelValidations.isValid) {
			confirmMultichannelRef.current.focus();
		}
	};

	const handleConfirmMultichannelChange = (value = '') => {
		getCFPInputProps('confirmMultichannel').onChange?.(value);
		setErrors({});
	};

	const handleFormSubmit = async (values: MultichannelFormValues) => {
		LoggerHelper.debug('App', 'MultichannelForm > handleFormSubmit:', { values });

		globalLoader.show();

		const {
			userAlias,
			userAliasTypeId,
			returnUrl,
			sdcSessionId,
			sdcBusinessSessionId,
			geo,
			targetSystem,
			targetInfrastructure,
		} = accountLogin || {};
		const { updateCredentialType, certValid, certValidUntil } = accountChallenge || {};

		const credentialsChallenge: CredentialsChallenge[] =
			positionsToAsk?.map((position, index) => ({
				Position: position,
				Value: values.oldMultichannel?.[index],
			})) || [];

		const fingerprint = await FingerprintHelper.getFingerprint();

		AppHelper.submitForm(endpoints.accountUserCredentials, {
			language: language?.toUpperCase(),
			userAlias,
			userAliasTypeId: userAliasTypeId?.toString(),
			returnUrl,
			sdcSessionId,
			sdcBusinessSessionId,
			geo,
			targetSystem,
			targetInfrastructure,
			fingerprint,
			/* checks both null and undefined */
			...(certValid != null && { certValid: certValid.toString() }),
			...(certValidUntil != null && { certValidUntil }),
			challengeType: updateCredentialType?.toString(),
			credentialsChallenge: JSON.stringify(credentialsChallenge),
			newCredential: values.multichannel,
			newConfirmCredential: values.confirmMultichannel,
		});
	};

	const validateMultichannel = (value?: string): MultichannelValidations => {
		const errorMessage = ' ';

		const required = ValidationHelper.validateRequired<string>(errorMessage)(value);
		const noSequentialNumbers = validateSequentialNumbers(value, errorMessage);
		const noRepeatedNumbers = validateRepeatedNumbers(value, errorMessage);
		const allowedChars = ValidationHelper.validateRegex(ALLOWED_CHARS_REGEX, errorMessage)(value);
		const requiredLength =
			ValidationHelper.validateMinLength<string>(7, errorMessage)(value) ||
			ValidationHelper.validateMaxLength<string>(7, errorMessage)(value);

		const validations = {
			required,
			noSequentialNumbers,
			noRepeatedNumbers,
			allowedChars,
			requiredLength,
		};

		const isValid = !Object.values(validations).some((valid) => !!valid?.message);

		return {
			isValid,
			...validations,
		};
	};

	const validateSequentialNumbers = (value = '', errorMessage = ''): ValidationError<string> | undefined => {
		const invalid: string[] = ['01234567890123456789', '98765432109876543210'];

		const includesInvalid = invalid.some((inv) => inv.includes(value));

		if (value.length > 1 && includesInvalid) {
			return { value, code: ValidationErrorCode.invalid, message: errorMessage };
		}
	};

	const validateRepeatedNumbers = (value = '', errorMessage = ''): ValidationError<string> | undefined => {
		const invalid: string[] = [
			'00000000000000000000',
			'11111111111111111111',
			'22222222222222222222',
			'33333333333333333333',
			'44444444444444444444',
			'55555555555555555555',
			'66666666666666666666',
			'77777777777777777777',
			'88888888888888888888',
			'99999999999999999999',
		];

		const includesInvalid = invalid.some((inv) => inv.includes(value));

		if (value.length > 1 && includesInvalid) {
			return { value, code: ValidationErrorCode.invalid, message: errorMessage };
		}
	};

	const multichannelValidations = validateMultichannel(getCFPInputProps('multichannel').value);
	const oldMultichannel = getCFPInputProps('oldMultichannel').value;
	const positionsToAsk = accountChallenge?.positionsToAsk;

	const isFormValid =
		oldMultichannel?.length === positionsToAsk?.length &&
		getCFPInputProps('multichannel').value &&
		getCFPInputProps('confirmMultichannel').value &&
		multichannelValidations.isValid &&
		isValid;

	return {
		isFormValid,
		multichannelValidations,
		confirmMultichannelRef,
		accountUserCredentialsErrorMessage,
		positionsToAsk,
		t,
		handleMultichannelChange,
		handleMultichannelComplete,
		handleConfirmMultichannelChange,
		getCFPInputProps,
		submitForm,
	};
};
